import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePage } from '../../hooks/usePage';
import {
  shopApi,
  Vendor,
  VendorStatus,
  CreateVendorDto,
} from '../../api/shop';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { Modal } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import {
  GoogleAddressAutocomplete,
  GoogleAddressSelection,
} from '../../components/ui/GoogleAddressAutocomplete';
import { PageHeader } from '../../components/ui/PageHeader';
import { Table } from '../../components/ui/Table';
import { VendorAvatar } from '../../components/ui/VendorAvatar';
import { useToast } from '../../hooks/useToast';

const STATUS_VARIANTS: Record<
  VendorStatus,
  'success' | 'warning' | 'danger' | 'neutral'
> = {
  active: 'success',
  pending: 'warning',
  suspended: 'danger',
};

const formatStatus = (s: VendorStatus) => s.charAt(0).toUpperCase() + s.slice(1);

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-NG', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const formatNgn = (n: number) =>
  new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    maximumFractionDigits: 0,
  }).format(n);

const formatAdminName = (vendor: Vendor) => {
  const admin = vendor.onboardedBy;
  return admin ? `${admin.firstName} ${admin.lastName}`.trim() : null;
};

const selectStyle: React.CSSProperties = {
  height: 40,
  padding: '0 32px 0 12px',
  fontSize: 14,
  fontFamily: 'var(--font-family)',
  color: 'var(--neutral-800)',
  background: 'var(--neutral-100)',
  border: '1px solid var(--neutral-300)',
  borderRadius: 'var(--radius-md)',
  outline: 'none',
  appearance: 'none',
  backgroundImage:
    "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none'%3E%3Cpath d='M3 4.5L6 7.5L9 4.5' stroke='%23566472' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 10px center',
  cursor: 'pointer',
};

export const Vendors: React.FC = () => {
  usePage('Vendors');
  const toast = useToast();
  const navigate = useNavigate();

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<VendorStatus | 'all'>('all');
  const [search, setSearch] = useState('');

  // Create vendor modal state
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<CreateVendorDto>({
    name: '',
    ownerEmail: '',
    ownerPhone: '',
    location: '',
    tagline: '',
    freeDelivery: false,
    provisionLogin: true,
  });
  const [addressSelection, setAddressSelection] =
    useState<GoogleAddressSelection | null>(null);
  const [addressError, setAddressError] = useState<string | null>(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [lastCreated, setLastCreated] = useState<Vendor | null>(null);

  // Reset-login state
  const [resetTarget, setResetTarget] = useState<Vendor | null>(null);
  const [resetLoading, setResetLoading] = useState(false);

  // Delete vendor state
  const [deleteTarget, setDeleteTarget] = useState<Vendor | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchVendors = useCallback(async () => {
    try {
      setLoading(true);
      const data = await shopApi.listVendors({
        status: statusFilter === 'all' ? undefined : statusFilter,
        q: search.trim() || undefined,
      });
      setVendors(data ?? []);
    } catch (err) {
      toast.error(err, 'Failed to load vendors.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search, toast]);

  useEffect(() => {
    void fetchVendors();
  }, [fetchVendors]);

  function closeCreateModal() {
    setShowCreate(false);
    setAddressSelection(null);
    setAddressError(null);
  }

  async function handleCreate() {
    if (!form.name.trim()) {
      toast.warning('Vendor name is required.');
      return;
    }
    if (form.provisionLogin && !form.ownerEmail?.trim()) {
      toast.warning('Owner email is required to provision a login.');
      return;
    }
    if (!addressSelection) {
      setAddressError('Select a Google address from the suggestions.');
      toast.warning('Select a validated vendor address.');
      return;
    }
    try {
      setCreateLoading(true);
      const created = await shopApi.createVendor({
        ...form,
        name: form.name.trim(),
        ownerEmail: form.ownerEmail?.trim() || undefined,
        ownerPhone: form.ownerPhone?.trim() || undefined,
        location: addressSelection.formattedAddress,
        pickupAddressLine: addressSelection.addressLine,
        pickupCity: addressSelection.city,
        pickupState: addressSelection.state,
        pickupPhone: form.ownerPhone?.trim() || undefined,
        pickupLat: addressSelection.lat,
        pickupLng: addressSelection.lng,
        pickupGooglePlaceId: addressSelection.placeId,
        tagline: form.tagline?.trim() || undefined,
      });
      closeCreateModal();
      setForm({
        name: '',
        ownerEmail: '',
        ownerPhone: '',
        location: '',
        tagline: '',
        freeDelivery: false,
        provisionLogin: true,
      });
      setLastCreated(created);
      if (created.loginProvisioned) {
        toast.success(
          created.loginProvisioned.emailSent
            ? 'Vendor created and welcome email sent.'
            : 'Vendor created, but the welcome email failed — use Reset login to retry.',
        );
      } else {
        toast.success('Vendor created.');
      }
      void fetchVendors();
    } catch (err) {
      toast.error(err, 'Failed to create vendor.');
    } finally {
      setCreateLoading(false);
    }
  }

  async function handleResetLogin() {
    if (!resetTarget) return;
    try {
      setResetLoading(true);
      const result = await shopApi.resetVendorLogin(resetTarget.id);
      toast.success(
        result.emailSent
          ? `New temp password emailed to ${resetTarget.ownerEmail}.`
          : 'Login reset, but the email failed to send. Try again or contact engineering.',
      );
      setResetTarget(null);
    } catch (err) {
      toast.error(err, 'Failed to reset vendor login.');
    } finally {
      setResetLoading(false);
    }
  }

  async function handleDeleteVendor() {
    if (!deleteTarget) return;
    try {
      setDeleteLoading(true);
      await shopApi.deleteVendor(deleteTarget.id);
      toast.success(`${deleteTarget.name} deleted.`);
      setDeleteTarget(null);
      void fetchVendors();
    } catch (err) {
      toast.error(err, 'Failed to delete vendor.');
    } finally {
      setDeleteLoading(false);
    }
  }

  async function handleStatusChange(vendor: Vendor, status: VendorStatus) {
    try {
      await shopApi.updateVendor(vendor.id, { status });
      toast.success(`Vendor marked ${formatStatus(status)}.`);
      void fetchVendors();
    } catch (err) {
      toast.error(err, 'Failed to update vendor status.');
    }
  }

  return (
    <>
      <PageHeader
        title="Vendors"
        description="Marketplace merchants. Creating a vendor here also provisions a Vendor Dashboard login and emails the temp password."
        actions={
          <Button onClick={() => setShowCreate(true)}>+ New Vendor</Button>
        }
      />

      {/* Filters */}
      <Card>
        <div
          style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            alignItems: 'flex-end',
          }}
        >
          <div style={{ flex: 1, minWidth: 220 }}>
            <Input
              label="Search"
              placeholder="Search by vendor name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div style={{ minWidth: 180 }}>
            <label
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: 'var(--neutral-700)',
                display: 'block',
                marginBottom: 6,
              }}
            >
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as VendorStatus | 'all')
              }
              style={selectStyle}
            >
              <option value="all">All statuses</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
        </div>
      </Card>

      <div style={{ marginTop: 16 }}>
        <Card>
          {loading ? (
            <div
              style={{
                padding: 48,
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Spinner />
            </div>
          ) : (
            <Table<Vendor>
              data={vendors}
              emptyMessage="No vendors yet. Create one to get started."
              columns={[
                {
                  key: 'name',
                  header: 'Vendor',
                  render: (v) => (
                    <button
                      type="button"
                      onClick={() => navigate(`/vendors/${v.id}`)}
                      style={{
                        background: 'none',
                        border: 'none',
                        padding: 0,
                        textAlign: 'left',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                      }}
                    >
                      <VendorAvatar name={v.name} avatarUrl={v.avatarUrl} size={32} />
                      <div>
                      <div style={{ fontWeight: 600, color: 'var(--primary-600, #2563EB)' }}>
                        {v.name}
                      </div>
                      <div
                        style={{
                          fontSize: 12,
                          color: 'var(--neutral-500)',
                          marginTop: 2,
                        }}
                      >
                        {v.slug}
                      </div>
                      </div>
                    </button>
                  ),
                },
                {
                  key: 'owner',
                  header: 'Owner',
                  render: (v) => (
                    <div style={{ fontSize: 13 }}>
                      <div>{v.ownerEmail ?? '—'}</div>
                      {v.ownerPhone ? (
                        <div
                          style={{
                            fontSize: 12,
                            color: 'var(--neutral-500)',
                          }}
                        >
                          {v.ownerPhone}
                        </div>
                      ) : null}
                    </div>
                  ),
                },
                {
                  key: 'onboardedBy',
                  header: 'Onboarded by',
                  render: (v) => {
                    const adminName = formatAdminName(v);
                    return adminName ? (
                      <div style={{ fontSize: 13 }}>
                        <div style={{ fontWeight: 600 }}>{adminName}</div>
                        <div
                          style={{
                            fontSize: 12,
                            color: 'var(--neutral-500)',
                            marginTop: 2,
                          }}
                        >
                          {formatDate(v.createdAt)}
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: 'var(--neutral-400)', fontSize: 13 }}>
                        Not recorded
                      </span>
                    );
                  },
                },
                {
                  key: 'status',
                  header: 'Status',
                  render: (v) => (
                    <Badge variant={STATUS_VARIANTS[v.status]}>
                      {formatStatus(v.status)}
                    </Badge>
                  ),
                },
                {
                  key: 'payout',
                  header: 'Payout balance',
                  render: (v) => (
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {formatNgn(Number(v.payoutBalance ?? 0))}
                    </span>
                  ),
                },
                {
                  key: 'created',
                  header: 'Created',
                  render: (v) => formatDate(v.createdAt),
                },
                {
                  key: 'actions',
                  header: '',
                  render: (v) => (
                    <div
                      style={{
                        display: 'flex',
                        gap: 6,
                        justifyContent: 'flex-end',
                      }}
                    >
                      {v.status === 'pending' ? (
                        <Button
                          variant="secondary"
                          onClick={() => handleStatusChange(v, 'active')}
                        >
                          Approve
                        </Button>
                      ) : v.status === 'active' ? (
                        <Button
                          variant="secondary"
                          onClick={() => handleStatusChange(v, 'suspended')}
                        >
                          Suspend
                        </Button>
                      ) : (
                        <Button
                          variant="secondary"
                          onClick={() => handleStatusChange(v, 'active')}
                        >
                          Reactivate
                        </Button>
                      )}
                      <Button
                        variant="secondary"
                        disabled={!v.ownerEmail}
                        onClick={() => setResetTarget(v)}
                      >
                        Reset login
                      </Button>
                      <Button
                        variant="danger"
                        onClick={() => setDeleteTarget(v)}
                      >
                        Delete
                      </Button>
                    </div>
                  ),
                },
              ]}
            />
          )}
        </Card>
      </div>

      <Modal
        isOpen={!!deleteTarget}
        onClose={() => {
          if (!deleteLoading) setDeleteTarget(null);
        }}
        title="Delete vendor"
        maxWidth={420}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <p
            style={{
              margin: 0,
              color: 'var(--neutral-700)',
              lineHeight: 1.5,
              fontSize: 14,
            }}
          >
            This permanently deletes{' '}
            <strong>{deleteTarget?.name ?? 'this vendor'}</strong>. Vendors with
            orders, escrow history, or payout history cannot be deleted; suspend
            them instead to hide them from the marketplace.
          </p>
          <div
            style={{
              display: 'flex',
              gap: 10,
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
            }}
          >
            <Button
              variant="secondary"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteLoading}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={deleteLoading}
              onClick={handleDeleteVendor}
            >
              Delete vendor
            </Button>
          </div>
        </div>
      </Modal>

      {/* Create vendor modal */}
      <Modal
        isOpen={showCreate}
        onClose={closeCreateModal}
        title="Create vendor"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Input
            label="Vendor name *"
            placeholder="e.g. Nivea Store"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <Input
            label="Owner email"
            type="email"
            placeholder="owner@vendor.com"
            value={form.ownerEmail}
            onChange={(e) =>
              setForm((f) => ({ ...f, ownerEmail: e.target.value }))
            }
            hint={
              form.provisionLogin
                ? 'A temporary password will be emailed to this address.'
                : 'Optional. No login will be provisioned.'
            }
          />
          <Input
            label="Owner phone"
            placeholder="+234…"
            value={form.ownerPhone}
            onChange={(e) =>
              setForm((f) => ({ ...f, ownerPhone: e.target.value }))
            }
          />
          <GoogleAddressAutocomplete
            label="Vendor address *"
            placeholder="Start typing the vendor pickup address"
            value={form.location ?? ''}
            error={addressError}
            onChange={(value) => {
              setForm((f) => ({ ...f, location: value }));
              setAddressSelection(null);
              setAddressError(value.trim() ? 'Select a Google address from the suggestions.' : null);
            }}
            onSelect={(selection) => {
              setAddressSelection(selection);
              setAddressError(null);
            }}
          />
          <Input
            label="Tagline"
            placeholder="One-liner shown on the vendor card"
            value={form.tagline}
            onChange={(e) =>
              setForm((f) => ({ ...f, tagline: e.target.value }))
            }
          />

          <label
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              fontSize: 13,
              color: 'var(--neutral-700)',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={!!form.freeDelivery}
              onChange={(e) =>
                setForm((f) => ({ ...f, freeDelivery: e.target.checked }))
              }
            />
            Vendor offers free delivery
          </label>

          <label
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              fontSize: 13,
              color: 'var(--neutral-700)',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={!!form.provisionLogin}
              onChange={(e) =>
                setForm((f) => ({ ...f, provisionLogin: e.target.checked }))
              }
            />
            Create Vendor Dashboard login + email temp password
          </label>

          <div
            style={{
              display: 'flex',
              gap: 8,
              justifyContent: 'flex-end',
              marginTop: 8,
            }}
          >
            <Button
              variant="secondary"
              onClick={closeCreateModal}
              disabled={createLoading}
            >
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={createLoading}>
              {createLoading ? 'Creating…' : 'Create vendor'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Last created confirmation */}
      <Modal
        isOpen={!!lastCreated}
        onClose={() => setLastCreated(null)}
        title="Vendor created"
      >
        {lastCreated ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ color: 'var(--neutral-700)' }}>
              <strong>{lastCreated.name}</strong> ({lastCreated.slug}) is now in
              the system with status{' '}
              <Badge variant={STATUS_VARIANTS[lastCreated.status]}>
                {formatStatus(lastCreated.status)}
              </Badge>
              .
            </p>
            {lastCreated.loginProvisioned ? (
              <p
                style={{
                  background: lastCreated.loginProvisioned.emailSent
                    ? 'var(--green-100, #ECFBF1)'
                    : 'var(--warning-100, #FEF3C7)',
                  color: lastCreated.loginProvisioned.emailSent
                    ? 'var(--success-600, #209347)'
                    : 'var(--warning-600, #B45309)',
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 13,
                }}
              >
                {lastCreated.loginProvisioned.emailSent
                  ? `Welcome email sent to ${lastCreated.ownerEmail}. They can sign into the Vendor Dashboard with the temp password.`
                  : `Login was created but the welcome email failed to send. Use "Reset login" to retry.`}
              </p>
            ) : (
              <p
                style={{
                  background: 'var(--neutral-200)',
                  color: 'var(--neutral-700)',
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 13,
                }}
              >
                No dashboard login was provisioned. Use "Reset login" later
                once an owner email is set.
              </p>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={() => setLastCreated(null)}>Done</Button>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Reset login confirmation */}
      <Modal
        isOpen={!!resetTarget}
        onClose={() => (resetLoading ? null : setResetTarget(null))}
        title="Reset vendor login?"
      >
        {resetTarget ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ color: 'var(--neutral-700)' }}>
              This generates a fresh temporary password for{' '}
              <strong>{resetTarget.name}</strong> and emails it to{' '}
              <strong>{resetTarget.ownerEmail}</strong>. Any active dashboard
              sessions will be signed out.
            </p>
            <div
              style={{
                display: 'flex',
                gap: 8,
                justifyContent: 'flex-end',
              }}
            >
              <Button
                variant="secondary"
                onClick={() => setResetTarget(null)}
                disabled={resetLoading}
              >
                Cancel
              </Button>
              <Button onClick={handleResetLogin} disabled={resetLoading}>
                {resetLoading ? 'Resetting…' : 'Send new temp password'}
              </Button>
            </div>
          </div>
        ) : null}
      </Modal>
    </>
  );
};
